import React, { useCallback, useMemo, useState } from "react";
import { Alert, Button, Form, InputGroup } from "react-bootstrap";
import toast from "react-hot-toast";
import { updatePublicListing } from "../../services/publicListingService";
import {
  AppEntry,
  inferAppType,
  PublicListing,
} from "../../types/PublicListing";
import "./ManageView.scss";
import { HANDLE_REGEX, WizardFormData } from "./types";

function validateForm(formData: WizardFormData): string[] {
  const errors: string[] = [];
  if (!formData.companyName.trim()) {
    errors.push("Company name is required.");
  }
  if (!formData.websiteUrl.trim()) {
    errors.push("Website URL is required.");
  }
  if (!formData.handle.trim()) {
    errors.push("Handle is required.");
  } else if (!HANDLE_REGEX.test(formData.handle)) {
    errors.push("Handle must be lowercase letters, numbers, and hyphens only.");
  }
  return errors;
}

const ManageView: React.FC<{
  listing: PublicListing;
  projectId: string;
}> = ({ listing, projectId }) => {
  const [formData, setFormData] = useState<WizardFormData>({
    companyName: listing.companyName ?? "",
    revenueDisplay: listing.revenueDisplay,
    subscriptionsDisplay: listing.subscriptionsDisplay,
    countryDisplay: listing.countryDisplay,
    websiteUrl: listing.websiteUrl,
    contactEmail: listing.contactEmail ?? "",
    logoUrl: listing.logoUrl,
    handle: listing.handle,
    appEntries: listing.appEntries ?? [],
  });
  const [isSaving, setIsSaving] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const validationErrors = useMemo(() => validateForm(formData), [formData]);

  const handleChange = useCallback((updates: Partial<WizardFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  }, []);

  const addAppEntry = useCallback(() => {
    setFormData((prev) => ({
      ...prev,
      appEntries: [...prev.appEntries, { name: "", url: "", type: "web-app" }],
    }));
  }, []);

  const removeAppEntry = useCallback((index: number) => {
    setFormData((prev) => ({
      ...prev,
      appEntries: prev.appEntries.filter((_, i) => i !== index),
    }));
  }, []);

  const updateAppEntry = useCallback(
    (index: number, updates: Partial<AppEntry>) => {
      setFormData((prev) => ({
        ...prev,
        appEntries: prev.appEntries.map((entry, i) => {
          if (i !== index) return entry;
          const updated = { ...entry, ...updates };
          if (updates.url !== undefined) {
            updated.type = inferAppType(updates.url);
          }
          return updated;
        }),
      }));
    },
    [],
  );

  const handleSave = useCallback(async () => {
    const errors = validateForm(formData);
    if (errors.length > 0) {
      setShowErrors(true);
      return;
    }

    setIsSaving(true);
    try {
      const validEntries = formData.appEntries.filter(
        (e) => e.name.trim() && e.url.trim(),
      );
      await updatePublicListing(projectId, {
        companyName: formData.companyName.trim(),
        revenueDisplay: formData.revenueDisplay,
        subscriptionsDisplay: formData.subscriptionsDisplay,
        countryDisplay: formData.countryDisplay,
        websiteUrl: formData.websiteUrl,
        contactEmail: formData.contactEmail || "",
        logoUrl: formData.logoUrl,
        handle: formData.handle,
        appEntries: validEntries,
      });
      toast.success("Public listing updated");
      setShowErrors(false);
    } catch {
      toast.error("Failed to update listing");
    } finally {
      setIsSaving(false);
    }
  }, [projectId, formData]);

  return (
    <div className="manage-section">
      <div className="form-section">
        <div className="form-section-header">Display Options</div>
        <div className="radio-group">
          <div className="radio-group-label">Revenue</div>
          <Form.Check
            type="radio"
            id="manage-revenue-trendline"
            label="Show trendline"
            name="manage-revenueDisplay"
            checked={formData.revenueDisplay === "trendline"}
            onChange={() => handleChange({ revenueDisplay: "trendline" })}
          />
          <Form.Check
            type="radio"
            id="manage-revenue-trendline-amounts"
            label="Show trendline with amounts"
            name="manage-revenueDisplay"
            checked={formData.revenueDisplay === "trendline-with-amounts"}
            onChange={() =>
              handleChange({ revenueDisplay: "trendline-with-amounts" })
            }
          />
          <Form.Check
            type="radio"
            id="manage-revenue-hidden"
            label="Don't show"
            name="manage-revenueDisplay"
            checked={formData.revenueDisplay === "hidden"}
            onChange={() => handleChange({ revenueDisplay: "hidden" })}
          />
        </div>

        <div className="radio-group">
          <div className="radio-group-label">Subscriptions</div>
          <Form.Check
            type="radio"
            id="manage-subscriptions-trendline"
            label="Show trendline"
            name="manage-subscriptionsDisplay"
            checked={formData.subscriptionsDisplay === "trendline"}
            onChange={() => handleChange({ subscriptionsDisplay: "trendline" })}
          />
          <Form.Check
            type="radio"
            id="manage-subscriptions-trendline-amounts"
            label="Show trendline with amounts"
            name="manage-subscriptionsDisplay"
            checked={formData.subscriptionsDisplay === "trendline-with-amounts"}
            onChange={() =>
              handleChange({ subscriptionsDisplay: "trendline-with-amounts" })
            }
          />
          <Form.Check
            type="radio"
            id="manage-subscriptions-hidden"
            label="Don't show"
            name="manage-subscriptionsDisplay"
            checked={formData.subscriptionsDisplay === "hidden"}
            onChange={() => handleChange({ subscriptionsDisplay: "hidden" })}
          />
        </div>

        <div className="radio-group">
          <div className="radio-group-label">Customer Country Distribution</div>
          <Form.Check
            type="radio"
            id="manage-country-top3"
            label="Show top 3 countries"
            name="manage-countryDisplay"
            checked={formData.countryDisplay === "top3"}
            onChange={() => handleChange({ countryDisplay: "top3" })}
          />
          <Form.Check
            type="radio"
            id="manage-country-top10"
            label="Show top 10 countries"
            name="manage-countryDisplay"
            checked={formData.countryDisplay === "top10"}
            onChange={() => handleChange({ countryDisplay: "top10" })}
          />
          <Form.Check
            type="radio"
            id="manage-country-hidden"
            label="Don't show"
            name="manage-countryDisplay"
            checked={formData.countryDisplay === "hidden"}
            onChange={() => handleChange({ countryDisplay: "hidden" })}
          />
        </div>
      </div>

      <div className="form-section">
        <div className="form-section-header">Business Info</div>

        <Form.Group className="form-group">
          <Form.Label htmlFor="manage-companyName">Company Name</Form.Label>
          <Form.Control
            id="manage-companyName"
            name="companyName"
            type="text"
            value={formData.companyName}
            isInvalid={showErrors && !formData.companyName.trim()}
            onChange={(e) => handleChange({ companyName: e.target.value })}
          />
          <Form.Control.Feedback type="invalid">
            Company name is required.
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="form-group">
          <Form.Label htmlFor="manage-websiteUrl">
            URL to Startup or App
          </Form.Label>
          <Form.Control
            id="manage-websiteUrl"
            name="websiteUrl"
            type="url"
            value={formData.websiteUrl}
            isInvalid={showErrors && !formData.websiteUrl.trim()}
            onChange={(e) => handleChange({ websiteUrl: e.target.value })}
          />
          <Form.Control.Feedback type="invalid">
            Website URL is required.
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="form-group">
          <Form.Label htmlFor="manage-contactEmail">
            Contact Email (optional)
          </Form.Label>
          <Form.Control
            id="manage-contactEmail"
            name="contactEmail"
            type="email"
            value={formData.contactEmail}
            onChange={(e) => handleChange({ contactEmail: e.target.value })}
          />
        </Form.Group>

        <Form.Group className="form-group">
          <Form.Label htmlFor="manage-logoUrl">URL to Company Logo</Form.Label>
          <Form.Control
            id="manage-logoUrl"
            name="logoUrl"
            type="url"
            value={formData.logoUrl}
            onChange={(e) => handleChange({ logoUrl: e.target.value })}
          />
        </Form.Group>
      </div>

      <div className="form-section">
        <div className="form-section-header">Apps</div>
        <p className="form-section-description">
          Add the apps and products that contribute to your revenue.
        </p>

        {formData.appEntries.map((entry, index) => (
          <div key={index} className="app-entry-row">
            <div className="app-entry-fields">
              <Form.Group className="app-entry-field">
                <Form.Label htmlFor={`manage-app-name-${index}`}>
                  Name
                </Form.Label>
                <Form.Control
                  id={`manage-app-name-${index}`}
                  name={`app-name-${index}`}
                  type="text"
                  placeholder="My App"
                  value={entry.name}
                  onChange={(e) =>
                    updateAppEntry(index, { name: e.target.value })
                  }
                />
              </Form.Group>
              <Form.Group className="app-entry-field app-entry-field--url">
                <Form.Label htmlFor={`manage-app-url-${index}`}>URL</Form.Label>
                <Form.Control
                  id={`manage-app-url-${index}`}
                  name={`app-url-${index}`}
                  type="url"
                  placeholder="https://..."
                  value={entry.url}
                  onChange={(e) =>
                    updateAppEntry(index, { url: e.target.value })
                  }
                />
                {entry.url && entry.type !== "web-app" && (
                  <Form.Text muted>Type: {entry.type}</Form.Text>
                )}
              </Form.Group>
            </div>
            <div className="app-entry-meta">
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => removeAppEntry(index)}
              >
                Remove
              </Button>
            </div>
          </div>
        ))}

        <Button variant="outline-primary" size="sm" onClick={addAppEntry}>
          + Add App
        </Button>
      </div>

      <div className="form-section">
        <div className="form-section-header">Handle</div>

        <Form.Group>
          <InputGroup>
            <InputGroup.Text className="handle-prefix">
              vcdb.app/company/
            </InputGroup.Text>
            <Form.Control
              id="manage-handle"
              name="handle"
              type="text"
              value={formData.handle}
              isInvalid={
                showErrors &&
                (!formData.handle.trim() || !HANDLE_REGEX.test(formData.handle))
              }
              onChange={(e) =>
                handleChange({ handle: e.target.value.toLowerCase().trim() })
              }
            />
            <Form.Control.Feedback type="invalid">
              {!formData.handle.trim()
                ? "Handle is required."
                : "Lowercase letters, numbers, and hyphens only."}
            </Form.Control.Feedback>
          </InputGroup>
        </Form.Group>
      </div>

      {showErrors && validationErrors.length > 0 && (
        <div className="form-section">
          <Alert variant="danger">
            {validationErrors.map((err) => (
              <div key={err}>{err}</div>
            ))}
          </Alert>
        </div>
      )}

      <div className="form-section-footer">
        <Button variant="primary" onClick={handleSave} disabled={isSaving}>
          {isSaving ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </div>
  );
};

export default ManageView;
